import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Shield,
  ChevronRight,
  HelpCircle,
  ExternalLink,
  BookOpenText,
  Users,
  KanbanSquare,
  CalendarRange,
  BriefcaseBusiness,
  Radar,
} from 'lucide-react';
import { useAuth } from '@lib/auth';
import { featureFlags } from '@lib/featureFlags';
import { useUIStore } from '@stores/ui.store';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
}

const adminSection: NavSection = {
  title: 'Admin',
  items: [
    { to: '/admin', label: 'Admin Panel', icon: <Shield size={20} /> },
    { to: '/reporting/deal-stages', label: 'Reporting', icon: <BriefcaseBusiness size={20} /> },
    { to: '/admin/wiki', label: 'Wiki Admin', icon: <BookOpenText size={20} /> },
    { to: '/kanban', label: 'YBH Kanban', icon: <KanbanSquare size={20} /> },
    { to: '/kanban/weekly', label: 'Kanban Weekly', icon: <CalendarRange size={20} /> },
  ],
  adminOnly: true,
};

export function Sidebar(): JSX.Element {
  const { user } = useAuth();
  const { sidebarOpen } = useUIStore();

  const isAdmin = user?.role === 'admin';
  const salesEnabled = featureFlags.salesOsV2Enabled;
  const navSections: NavSection[] = [
    {
      title: 'Main',
      items: salesEnabled
        ? [
            { to: '/dashboard', label: 'Home', icon: <LayoutDashboard size={20} /> },
            { to: '/sales/activity', label: 'Activity', icon: <Radar size={20} /> },
            { to: '/sales/opportunities', label: 'Opportunities', icon: <BriefcaseBusiness size={20} /> },
            { to: '/contacts', label: 'Contacts', icon: <Users size={20} /> },
          ]
        : [
            { to: '/dashboard', label: 'Home', icon: <LayoutDashboard size={20} /> },
            { to: '/contacts', label: 'Contacts', icon: <Users size={20} /> },
        ],
    },
    {
      title: 'Podcast Hub',
      items: [
        { to: '/podcasts/guests', label: 'Guests', icon: <Users size={20} /> },
        { to: '/podcasts/calendar', label: 'Calendar', icon: <CalendarRange size={20} /> },
      ],
    },
  ];

  return (
    <aside
      role="navigation"
      aria-label="Main navigation"
      className={`
        bg-ybh-bg-secondary border-r border-[var(--color-divider)]
        flex flex-col
        transition-[width] duration-300 ease-out
        ${sidebarOpen ? 'w-64' : 'w-20'}
      `}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-3 border-b border-[var(--color-divider)]">
        <div className="flex items-center gap-2">
          <img
            src="/images/pulse-icon.png"
            alt="Pulse"
            className="w-12 h-12 object-contain"
          />
          {sidebarOpen && (
            <span className="font-display text-xl text-ybh-text-primary tracking-wider">
              PULSE
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto" aria-label="Primary">
        {navSections.map((section, sectionIndex) => (
          <div key={section.title} className={sectionIndex > 0 ? 'mt-4' : ''}>
            {/* Section header */}
            {sidebarOpen && (
              <div className="flex items-center gap-2 px-3 mb-2">
                <span className="text-[10px] font-semibold text-ybh-text-muted/60 tracking-widest uppercase">
                  {section.title}
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-ybh-text-muted/20 to-transparent" />
              </div>
            )}
            {!sidebarOpen && sectionIndex > 0 && (
              <div className="mx-3 mb-2 h-px bg-ybh-text-muted/10" />
            )}
            <ul className="space-y-0.5" role="list">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `
                      group flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200 ease-out relative
                      ${isActive
                        ? 'bg-ybh-orange/10 text-ybh-text-primary'
                        : 'text-ybh-text-muted hover:bg-ybh-bg-tertiary hover:text-ybh-text-primary'
                      }
                    `}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-ybh-gradient rounded-r-full" />
                        )}
                        <span className={`transition-colors ${isActive ? 'text-ybh-orange' : ''}`}>
                          {item.icon}
                        </span>
                        {sidebarOpen && (
                          <>
                            <span className="font-medium text-sm flex-1">{item.label}</span>
                            <ChevronRight
                              size={16}
                              className={`
                                transition-all duration-200
                                ${isActive
                                  ? 'opacity-100 text-ybh-orange'
                                  : 'opacity-0 group-hover:opacity-50'
                                }
                              `}
                            />
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Admin section */}
        {isAdmin && (
          <div className="mt-4">
            {sidebarOpen && (
              <div className="flex items-center gap-2 px-3 mb-2">
                <span className="text-[10px] font-semibold text-ybh-warning/60 tracking-widest uppercase">
                  {adminSection.title}
                </span>
                <div className="flex-1 h-px bg-gradient-to-r from-ybh-warning/20 to-transparent" />
              </div>
            )}
            {!sidebarOpen && (
              <div className="mx-3 mb-2 h-px bg-ybh-warning/20" />
            )}
            <ul className="space-y-0.5">
              {adminSection.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `
                      group flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200 ease-out relative
                      ${isActive
                        ? 'bg-ybh-warning/10 text-ybh-text-primary'
                        : 'text-ybh-text-muted hover:bg-ybh-bg-tertiary hover:text-ybh-text-primary'
                      }
                    `}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-ybh-warning rounded-r-full" />
                        )}
                        <span className={`transition-colors ${isActive ? 'text-ybh-warning' : ''}`}>
                          {item.icon}
                        </span>
                        {sidebarOpen && (
                          <>
                            <span className="font-medium text-sm flex-1">{item.label}</span>
                            <ChevronRight
                              size={16}
                              className={`
                                transition-all duration-200
                                ${isActive
                                  ? 'opacity-100 text-ybh-warning'
                                  : 'opacity-0 group-hover:opacity-50'
                                }
                              `}
                            />
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* Help link */}
      <div className="px-3 pb-3">
        <a
          href="https://pulse-guide-d9t.pages.dev"
          target="_blank"
          rel="noopener noreferrer"
          className={`
            group flex items-center gap-3 px-3 py-3 rounded-lg
            transition-all duration-200 ease-out
            text-ybh-text-muted hover:bg-ybh-bg-tertiary hover:text-ybh-text-primary
          `}
        >
          <HelpCircle size={20} className="transition-colors group-hover:text-ybh-orange" />
          {sidebarOpen && (
            <>
              <span className="font-medium text-sm flex-1">User Guide</span>
              <ExternalLink size={14} className="opacity-50 group-hover:opacity-100" />
            </>
          )}
        </a>
      </div>

      {/* User section */}
      <div className="p-3 border-t border-transparent">
        <div className={`
          flex items-center gap-3 px-3 py-3 rounded-lg
          bg-ybh-bg-tertiary/50 transition-colors
          hover:bg-ybh-bg-tertiary
        `}>
          {/* Avatar with gradient border */}
          <div className="relative">
            <div className="absolute inset-0 bg-ybh-gradient rounded-full blur-sm opacity-50" />
            <div className="relative w-9 h-9 rounded-full bg-ybh-bg-elevated flex items-center justify-center border border-[var(--color-divider)]">
              <span className="text-ybh-orange font-semibold text-sm">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ybh-text-primary truncate">{user?.name}</p>
              <p className="text-xs text-ybh-text-muted truncate capitalize">{user?.role}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
